/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { Box, Card, CardContent, Tooltip, Typography } from "@mui/material";
import { GetAllFilesAndFoldersByUserDto } from "../../types/FolderPermission";
import { FolderFileType } from "../../data/Enums";
import ActionsMenu from "../../components/form/MenuAction/ActionsMenu";
import { format } from "date-fns";
import useMenuActions from "../../hooks/useMenuActions";
import { useUser } from "../../context/UserContext";
import EditResource from "../editResource/EditResource";
import ConfirmationDialog from "../../components/ui/ConfirmationDialog";
import UploadComponent from "../upload/UploadComponent";
import { GLOBALSETTINGS } from "../../data/constants";
import { ACTIONITEMVALIDATIONS } from "../../constants/messages";
import "./RecentViewedItems.scss";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { getFileExtension, renderSvgIcon } from "../../utils/commonHelper";
import ShareResource from "../share/ShareResource";
import { useDispatch } from "react-redux";
import { setSuggestedFolderSelected } from "../../redux/suggestedFolderSelectedSlice";
import BackdropProgressBar from "../../components/ui/BackdropProgressBar";
import { useDownload } from "../download/DownloadContext";

interface RecentViewedItemsProps {
  lastViewedItems: GetAllFilesAndFoldersByUserDto[] | [];
  folderFileType: FolderFileType;
}
const RecentViewedItems: React.FC<RecentViewedItemsProps> = ({
  lastViewedItems,
  folderFileType,
}) => {
  const [menuAnchors, setMenuAnchors] = useState<{ [key: number]: HTMLElement | null }>({});
  const [currentRow, setCurrentRow] = useState<GetAllFilesAndFoldersByUserDto | null>(null);
  const [loading, setLoading] = useState(false);
  const { userId } = useUser();
  const dispatch = useDispatch();
  const { startDownload } = useDownload();

  const handleMenuClose = (id: number) => {
    setMenuAnchors((prev) => ({ ...prev, [id]: null }));
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLButtonElement>,
    row: GetAllFilesAndFoldersByUserDto
  ) => {
    setMenuAnchors((prev) => ({
      ...prev,
      [row.folderId > 0 ? row.folderId : row.fileId]: event.currentTarget,
    }));
    setCurrentRow(row);
  };

  const { actions, states, setters } = useMenuActions(userId, currentRow, () =>
    handleMenuClose(currentRow?.folderId > 0 ? currentRow?.folderId : currentRow?.fileId),
  );
  useEffect(() => {
    if (
      states.dialogOpen &&
      actions.downloadData.userId === userId &&
      actions.downloadData.folderId === currentRow.folderId
    ) {
      startDownload(actions.downloadData.folderId, actions.downloadData.folderName);
    }
  }, [states.dialogOpen, actions.downloadData, userId]);
  const handleEditClose = () => {
    if (states.publish) {
      setters.setPublish(false);
    } else {
      setters.setEditDialogOpen(false);
    }
  };

  const handleAddNewClose = () => {
    setters.setAddNew(false);
  };

  const handleShareClose = (displayRoles: boolean) => {
    const params = new URLSearchParams(window.location.search);
    if (displayRoles) {
      setters.setShareToRole(false);
      params.delete("shareRole");
    } else {
      setters.setShareToUser(false);
      params.delete("shareUser");
    }
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    // Replace URL in the address bar without reloading
    window.history.replaceState({}, "", newUrl);
  };

  const handleDoubleClick = async (item: GetAllFilesAndFoldersByUserDto) => {
    if (folderFileType === FolderFileType.Folder) {
      setLoading(true);
      const folderId = item.folderId ?? GLOBALSETTINGS.DEFAULT_STATE;
      const parentFolderId =
        item.parentFolderId === 0 || item.parentFolderId === null
          ? GLOBALSETTINGS.DEFAULT_STATE
          : item.parentFolderId;
      if (folderId !== null) {
        dispatch(
          setSuggestedFolderSelected({ folderId: folderId, parentFolderId: parentFolderId })
        );
      }
    }
  };

  const settings = {
    dots: false,
    infinite: false, // Prevents wrapping, ensuring left alignment
    speed: 500,
    slidesToShow: 4, // Adjust this based on screen size
    slidesToScroll: 1,
    arrows: true,
    responsive: [
      {
        breakpoint: 1360,
        settings: {
          slidesToShow: 3,
        },
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  };
  const backgroundColors = ["#EBF3FE", "#E6FFFA", "#FBF2EF", "#FEF5E5"];
  return (
    <section className="mb-3">
      <Box>
        <BackdropProgressBar loading={loading} />
      </Box>
      <Card>
        <CardContent>
          <Typography variant="h5" component="div" className="mb-3 card-title">
            {folderFileType === FolderFileType.Folder ? "Suggested Folders" : "Suggested Files"}
          </Typography>

          <Slider {...settings} className="suggested-slider">
            {lastViewedItems.map((item, index) => {
              const bgColor = backgroundColors[index % backgroundColors.length];

              return (
                <div key={index} className="px-2">
                  <Card
                    sx={{
                      backgroundColor: bgColor,
                      cursor: folderFileType === FolderFileType.Folder ? "pointer" : "",
                    }}
                    variant="outlined"
                    className="quick-access-card"
                    onDoubleClick={() => handleDoubleClick(item)}
                  >
                    <ActionsMenu
                      currentRow={item}
                      menuAnchorEl={menuAnchors[item.folderId || item.fileId]}
                      handleMenuOpen={handleMenuOpen}
                      handleMenuClose={() => handleMenuClose(item.folderId || item.fileId)}
                      handleAction={actions.handleAction}
                      displayRightSide={true}
                    />

                    {folderFileType === FolderFileType.Folder ? (
                      <CardContent className="p-0 folders-card">
                        <div className="d-flex align-items-center folder-info">
                          <div className="folder-icon-wrapper">
                            <img src="/folder.svg" alt="documents-icon" className="folder-icon" />
                          </div>

                          <div className="folder-text">
                            <Tooltip title={item.description}>
                              <Typography
                                variant="h5"
                                className="title text-ellipsis"
                                sx={{ color: "text.primary" }}
                              >
                                {item.description}
                              </Typography>
                            </Tooltip>
                            <Tooltip
                              title={
                                item.createdDate
                                  ? `Date: ${format(item.createdDate, GLOBALSETTINGS.DATE_FORMAT_TIME)}`
                                  : ""
                              }
                            >
                              <Typography
                                variant="body2"
                                className="subtitle text-ellipsis"
                                sx={{ color: "text.secondary" }}
                              >
                                Date:{" "}
                                {item.createdDate
                                  ? format(item.createdDate, GLOBALSETTINGS.DATE_FORMAT_TIME)
                                  : ""}
                              </Typography>
                            </Tooltip>
                          </div>
                        </div>
                      </CardContent>
                    ) : (
                      <CardContent className="p-0 files-card">
                        <div className="d-flex align-items-center justify-content-center mb-2">
                          {renderSvgIcon(getFileExtension(item.description), "file-icon", 80)}
                        </div>
                        <Tooltip title={item.description}>
                          <Typography
                            variant="h5"
                            className="title text-center text-ellipsis user-select-text"
                            sx={{
                              color: "text.primary",
                            }}
                          >
                            {item.description}
                          </Typography>
                        </Tooltip>
                        <Tooltip
                          title={
                            item.createdDate
                              ? `Date: ${format(item.createdDate, GLOBALSETTINGS.DATE_FORMAT_TIME).replace(" ", " ")}`
                              : ""
                          }
                        >
                          <Typography
                            variant="body2"
                            className="subtitle text-center user-select-text"
                            sx={{
                              color: "text.secondary",
                              whiteSpace: "pre-wrap", // keep as-is for spacing
                            }}
                          >
                            Date:{" "}
                            {item.createdDate
                              ? format(item.createdDate, GLOBALSETTINGS.DATE_FORMAT_TIME).replace(
                                  " ",
                                  " "
                                )
                              : ""}
                          </Typography>
                        </Tooltip>
                      </CardContent>
                    )}
                  </Card>
                </div>
              );
            })}
          </Slider>

          <div>
            {states.editDialogOpen && (currentRow?.folderId || currentRow?.fileId) && (
              <EditResource
                type={currentRow?.type as string}
                typeId={
                  currentRow?.type === FolderFileType[FolderFileType.Folder]
                    ? currentRow?.folderId
                    : currentRow?.fileId
                }
                isPublish={false}
                onClose={handleEditClose}
                canManagePermissions={currentRow?.canManagePermissions}
              />
            )}
            <ConfirmationDialog
              open={states.deleteConfirm}
              title={ACTIONITEMVALIDATIONS.CONFIRM}
              message={ACTIONITEMVALIDATIONS.DELETE_RESOURCE}
              onClose={actions.handleDeleteConfirm}
            />
            <ConfirmationDialog
              open={states.unPublishConfirm}
              title={ACTIONITEMVALIDATIONS.CONFIRM}
              message={ACTIONITEMVALIDATIONS.UNPUBLISH_RESOURCE}
              onClose={actions.handleUnPublishConfirm}
            />
            {states.publish && (currentRow?.folderId || currentRow?.fileId) && (
              <EditResource
                type={currentRow?.type as string}
                typeId={
                  currentRow?.type === FolderFileType[FolderFileType.Folder]
                    ? currentRow?.folderId
                    : currentRow?.fileId
                }
                isPublish={true}
                onClose={handleEditClose}
                canManagePermissions={currentRow?.canManagePermissions}
              />
            )}
            {(states.shareToRole || states.shareToUser) &&
            currentRow && 
            (currentRow.folderId || currentRow.fileId) && (
                <ShareResource
                  type={currentRow?.type as string}
                  typeId={
                    currentRow?.type === FolderFileType[FolderFileType.Folder]
                      ? currentRow?.folderId
                      : currentRow?.fileId
                  }
                  parentFolderId={currentRow?.parentFolderId}
                  displayRoles={states.shareToRole}
                  fileUrl={currentRow?.fileUrl}
                  description={currentRow?.description}
                  onClose={() => handleShareClose(states.shareToRole)}
                  canDownload={!!currentRow?.canDownload}
                />
              )}
            {states.addNew && currentRow?.folderId && (
              <UploadComponent
                parentFolderId={currentRow?.folderId}
                onClose={handleAddNewClose}
                fromGrid={true}
              />
            )}
          </div>
        </CardContent>
      </Card>
    </section>
  );
};

export default RecentViewedItems;
